// Leads data layer (M1/M2/M5/M6). Money is integer paise; areas are square feet.
import { useQuery } from '@tanstack/react-query';
import { get } from '../../lib/api.js';
import { addDays, dayKey, DEFAULT_TIME_ZONE, weekStart, zonedInstant } from '../../lib/time.js';
import type { TimelineCall, TimelineMessage } from '../comms/TimelineEntries.js';

export type LeadStatus = 'ACTIVE' | 'DORMANT' | 'JUNK' | 'MERGED';
export type InterestStatus = 'OPEN' | 'WON' | 'LOST' | 'JUNK';
export type StageCategory = 'OPEN' | 'WON' | 'LOST';

export interface LeadSummary {
  id: string;
  name: string;
  primaryPhone: string | null;
  primaryEmail: string | null;
  city: string | null;
  status: LeadStatus;
  score: number;
  ownerId: string | null;
  owner: { id: string; displayName: string } | null;
  lastActivityAt: string | null;
  createdAt: string;
  tags: string[];
  version: number;
  interests: {
    id: string;
    projectId: string;
    status: InterestStatus;
    nextFollowUpAt: string | null;
    stage: { id: string; name: string; category: StageCategory; sortOrder?: number; colour?: string | null };
    project: { name: string };
    pipeline?: { name: string; projectId: string | null };
  }[];
  /** Where the person first came from (the list's Source column). */
  touches?: FirstTouch[];
  /** Extra fields; `kenyt` holds the profile a Kenyt import brought (designation, gender, …). */
  custom?: Record<string, unknown> | null;
  /** The list section the lead falls in (priority order, D-119). */
  group?: LeadGroup;
  /** Its soonest open follow-up. */
  followUpAt?: string | null;
  /** Site-visit status: the next upcoming visit, how many are done, how many are past and unmarked. */
  visits?: LeadVisits;
  /** When the person last enquired again, and when a member last acted on the lead. */
  reEnquiredAt?: string | null;
  attendedAt?: string | null;
  /** Whether the lead is waiting for a member (list rows). */
  attention?: LeadAttention;
}

export interface LeadAttention {
  /** When it started waiting: the later of its creation and its last re-enquiry. */
  since: string;
  /** Re-enquired and nobody has acted since. */
  reEnquired: boolean;
  /** Waiting for longer than the organisation's limit (24 h unless changed). */
  unattended: boolean;
  hours: number;
}

export interface LeadVisits {
  next: { id: string; scheduledAt: string; status: string; projectName: string } | null;
  done: number;
  overdue: number;
}

export type VisitFilter = '' | 'scheduled' | 'done' | 'overdue' | 'none';

export interface VisitSummary {
  scheduled: number;
  done: number;
  overdue: number;
  noShow: number;
  cancelled: number;
}

export type LeadGroup = 're_enquired' | 'followup' | 'new' | 'progress' | 'closed';
export type LeadSort = 'priority' | 'newest' | 'activity' | 'score';

export interface LeadListPage {
  items: LeadSummary[];
  total?: number;
  page?: number;
  pages?: number;
  groups?: Record<LeadGroup, number>;
  nextCursor?: string;
}

export interface FirstTouch {
  channel: string;
  source: string | null;
  subSource: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  campaignName: string | null;
  platform: string | null;
}

export interface LeadDetail extends Omit<LeadSummary, 'interests' | 'touches'> {
  language: string | null;
  isNri: boolean;
  notes: string | null;
  mergedIntoId: string | null;
  contacts: {
    id: string;
    type: 'PHONE' | 'EMAIL' | 'WHATSAPP';
    rawValue: string;
    normalisedValue: string;
    isPrimary: boolean;
  }[];
  requirements: {
    id: string;
    typology: string | null;
    budgetMin: number | null;
    budgetMax: number | null;
    areaMin: number | null;
    areaMax: number | null;
    facing: string | null;
    floorPreference: string | null;
    purpose: string | null;
    fundingMode: string | null;
    possessionTimeline: string | null;
    notes: string | null;
  }[];
  consentState: { channel: string; status: string; updatedAt: string }[];
  /** The company the person buys for (corporate buyers, D-116). */
  company: {
    id: string;
    name: string;
    websiteUrl: string | null;
    domain: string | null;
    size: string | null;
    websiteTraffic?: number | null;
    postalCode?: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    tags: string[];
  } | null;
  /** Co-applicants, spouses and other decision makers on the same enquiry. */
  parties: {
    id: string;
    role: string;
    name: string;
    phone: string | null;
    whatsapp: string | null;
    email: string | null;
    designation: string | null;
    gender?: string | null;
    birthday?: string | null;
    language?: string | null;
    city: string | null;
    state?: string | null;
    country: string | null;
    tags: string[];
  }[];
  touches: {
    id: string;
    channel: string;
    source: string | null;
    utmSource: string | null;
    utmCampaign: string | null;
    utmMedium: string | null;
    utmTerm?: string | null;
    utmContent?: string | null;
    referrer?: string | null;
    fbclid?: string | null;
    device?: string | null;
    city?: string | null;
    subSource: string | null;
    campaignId: string | null;
    adSetId: string | null;
    adId: string | null;
    keyword: string | null;
    gclid: string | null;
    campaignName: string | null;
    adSetName: string | null;
    adName: string | null;
    formId: string | null;
    formName: string | null;
    pageId: string | null;
    platform: string | null;
    externalLeadId: string | null;
    submittedAt: string | null;
    landingPage: string | null;
    isFirst: boolean;
    isLast: boolean;
    occurredAt: string;
  }[];
  assignments: {
    id: string;
    fromOwnerId: string | null;
    toOwnerId: string | null;
    source: string;
    reason: string | null;
    occurredAt: string;
  }[];
}

export interface Interest {
  id: string;
  leadId: string;
  projectId: string;
  project: { id: string; name: string; code: string };
  pipelineId: string;
  stageId: string;
  stage: {
    id: string;
    name: string;
    category: StageCategory;
    sortOrder: number;
    winProbability: number;
    colour?: string | null;
  };
  pipeline?: { id: string; name: string };
  ownerId: string | null;
  owner: { id: string; displayName: string } | null;
  status: InterestStatus;
  lossReason: { id: string; name: string } | null;
  lossNote: string | null;
  unitId: string | null;
  unit: { id: string; number: string; status: string } | null;
  source: string | null;
  nextFollowUpAt: string | null;
  firstResponseAt: string | null;
  stageChangedAt: string;
  version: number;
  /** Extra fields; `kenyt` holds what a Kenyt import brought (D-116). */
  custom?: Record<string, unknown> | null;
  lead?: { id: string; name: string; primaryPhone: string | null; score: number; status: LeadStatus };
}

export interface Activity {
  id: string;
  leadId: string | null;
  interestId: string | null;
  type: string;
  actorId: string | null;
  actorName: string | null;
  payload: Record<string, unknown>;
  occurredAt: string;
  /** The live call or message record for call / WhatsApp entries (status, disposition, recording). */
  call?: TimelineCall;
  message?: TimelineMessage;
}

export interface Stage {
  id: string;
  name: string;
  sortOrder: number;
  category: StageCategory;
  colour: string | null;
  slaMinutes: number | null;
  winProbability: number;
  mandatoryFields: string[];
  automations?: unknown;
}

export interface Pipeline {
  id: string;
  name: string;
  projectId: string | null;
  isDefault: boolean;
  /** The organisation's stage set, used by every project (D-134). */
  stageTemplate?: boolean;
  active: boolean;
  project?: { name: string } | null;
  stages: Stage[];
}

export interface BoardCard {
  id: string;
  leadId: string;
  stageId: string;
  ownerId: string | null;
  owner: { displayName: string } | null;
  nextFollowUpAt: string | null;
  stageChangedAt: string;
  unitId: string | null;
  unit: { number: string } | null;
  version: number;
  projectId: string;
  project: { name: string };
  /** Time in stage (M5), and whether it is past the stage's SLA. */
  secondsInStage: number;
  overStageSla: boolean;
  lead: { name: string; primaryPhone: string | null; score: number; city: string | null; tags: string[] };
}

export interface Board {
  pipeline: { id: string; name: string; projectId: string | null };
  /** `total`: every card in the stage; won and lost columns list only their newest 100. */
  stages: (Stage & { cards: BoardCard[]; total?: number })[];
}

export interface PipelineCount {
  pipelineId: string;
  open: number;
  closed: number;
}

/** Open and closed cards per pipeline, in the member's scope. */
export function usePipelineCounts() {
  return useQuery({
    queryKey: ['pipeline-counts'],
    queryFn: () => get<PipelineCount[]>('/pipeline-counts'),
  });
}

export interface Task {
  id: string;
  title: string;
  kind: string;
  status: 'OPEN' | 'DONE' | 'CANCELLED';
  priority: 'LOW' | 'NORMAL' | 'HIGH';
  dueAt: string;
  completedAt: string | null;
  outcome: string | null;
  notes: string | null;
  leadId: string | null;
  interestId: string | null;
  assigneeId: string;
  assignee: { id: string; displayName: string } | null;
  lead: { id: string; name: string; primaryPhone: string | null } | null;
}

export interface Today {
  overdue: Task[];
  dueToday: Task[];
  upcoming: Task[];
  followUps: {
    id: string;
    nextFollowUpAt: string | null;
    stage: { name: string };
    lead: { id: string; name: string; primaryPhone: string | null; score: number };
  }[];
  visits: {
    id: string;
    scheduledAt: string;
    status: string;
    lead: { id: string; name: string; primaryPhone: string | null };
    project: { id: string; name: string };
  }[];
}

export interface Visit {
  id: string;
  leadId: string;
  projectId: string;
  slotId: string | null;
  hostId: string | null;
  status: 'SCHEDULED' | 'CONFIRMED' | 'CHECKED_IN' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED';
  scheduledAt: string;
  checkInAt: string | null;
  /** How far from the project's pin the check-in was (D-140); null without a location or a pin. */
  checkInDistanceM?: number | null;
  lead: { id: string; name: string; primaryPhone: string | null };
  project: {
    id: string;
    name: string;
    address?: string | null;
    /** Decimal strings from the API. */
    latitude?: string | number | null;
    longitude?: string | number | null;
  };
  host: { id: string; displayName: string } | null;
  feedback: {
    rating: number | null;
    liked: string | null;
    concerns: string | null;
    nextStep: string | null;
  } | null;
}

export interface LossReason {
  id: string;
  name: string;
  category: string | null;
  requiresNote: boolean;
  active: boolean;
}

export const inr = (paise: number | null | undefined): string =>
  paise === null || paise === undefined
    ? '—'
    : `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(paise / 100)}`;

/** Compact Indian money for cards: ₹45.0 L, ₹1.2 Cr. */
export const inrShort = (paise: number | null | undefined): string => {
  if (paise === null || paise === undefined) return '—';
  const rupees = paise / 100;
  if (rupees >= 10_000_000) return `₹${(rupees / 10_000_000).toFixed(2).replace(/\.?0+$/, '')} Cr`;
  if (rupees >= 100_000) return `₹${(rupees / 100_000).toFixed(1).replace(/\.0$/, '')} L`;
  return inr(paise);
};

export const relativeTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const abs = Math.abs(diff);
  const units: [number, Intl.RelativeTimeFormatUnit][] = [
    [60_000, 'minute'],
    [3_600_000, 'hour'],
    [86_400_000, 'day'],
    [604_800_000, 'week'],
  ];
  const fmt = new Intl.RelativeTimeFormat('en-IN', { numeric: 'auto' });
  if (abs < 60_000) return 'just now';
  for (const [ms, unit] of units) {
    const next = unit === 'week' ? Infinity : ms * (unit === 'minute' ? 60 : unit === 'hour' ? 24 : 7);
    if (abs < next) return fmt.format(Math.round(-diff / ms), unit);
  }
  return new Date(iso).toLocaleDateString('en-IN');
};

export const scoreTone = (score: number): 'success' | 'warning' | 'neutral' =>
  score >= 50 ? 'success' : score >= 25 ? 'warning' : 'neutral';

export function useLeads(qs: string) {
  return useQuery({
    queryKey: ['leads', qs],
    queryFn: () => get<LeadListPage>(`/leads${qs ? `?${qs}` : ''}`),
    placeholderData: (prev) => prev,
  });
}

export interface AttentionSummary {
  unattendedMine: number;
  unattendedTeam: number;
  reEnquiredMine: number;
  afterHours: number;
}

/** Today's card: how many leads are unattended (mine, my team's) and re-enquired. */
export function useAttentionSummary(enabled = true) {
  return useQuery({
    queryKey: ['lead-attention'],
    queryFn: () => get<AttentionSummary>('/leads/attention-summary'),
    enabled,
    refetchInterval: 120_000,
  });
}

export function useLead(id: string | null) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => get<LeadDetail>(`/leads/${id!}`),
    enabled: Boolean(id),
  });
}

export function useTimeline(id: string | null) {
  return useQuery({
    queryKey: ['lead-timeline', id],
    queryFn: () => get<Activity[]>(`/leads/${id!}/timeline`),
    enabled: Boolean(id),
  });
}

export function useLeadInterests(leadId: string | null) {
  return useQuery({
    queryKey: ['interests', 'lead', leadId],
    queryFn: () => get<Interest[]>(`/interests?leadId=${leadId!}`),
    enabled: Boolean(leadId),
  });
}

export function usePipelines(projectId?: string) {
  return useQuery({
    queryKey: ['pipelines', projectId ?? 'all'],
    queryFn: () => get<Pipeline[]>(`/pipelines${projectId ? `?projectId=${projectId}` : ''}`),
  });
}

export function useBoard(pipelineId: string | null, qs = '') {
  return useQuery({
    queryKey: ['board', pipelineId, qs],
    queryFn: () => get<Board>(`/pipelines/${pipelineId!}/board${qs ? `?${qs}` : ''}`),
    enabled: Boolean(pipelineId),
    placeholderData: (prev) => prev,
  });
}

export function useToday() {
  return useQuery({ queryKey: ['today'], queryFn: () => get<Today>('/today') });
}

export function useTasks(query: { status?: string; leadId?: string } = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v) params.set(k, String(v));
  const qs = params.toString();
  return useQuery({ queryKey: ['tasks', qs], queryFn: () => get<Task[]>(`/tasks${qs ? `?${qs}` : ''}`) });
}

export function useVisits(query: { leadId?: string; projectId?: string; status?: string } = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v) params.set(k, String(v));
  const qs = params.toString();
  return useQuery({ queryKey: ['visits', qs], queryFn: () => get<Visit[]>(`/visits${qs ? `?${qs}` : ''}`) });
}

/** Counts of visits scheduled, done, overdue, no-show and cancelled; `range` limits them to from..to. */
export function useVisitSummary(range: { from?: string; to?: string } = {}, enabled = true) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(range)) if (v) params.set(k, v);
  const qs = params.toString();
  return useQuery({
    queryKey: ['visits', 'summary', qs],
    queryFn: () => get<VisitSummary>(`/visits/summary${qs ? `?${qs}` : ''}`),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useLossReasons() {
  return useQuery({ queryKey: ['loss-reasons'], queryFn: () => get<LossReason[]>('/loss-reasons') });
}

// ───────────────────────────────────────────── leads UX (leaf 1.2.2)

/** What a saved view stores; `ownerId: 'me'` means whoever is looking. */
export interface ViewFilters {
  q?: string;
  /** Source, UTM source or campaign (part of the name). */
  source?: string;
  /** List order; priority when left out. */
  sort?: LeadSort;
  ownerId?: string;
  unassigned?: boolean;
  projectId?: string;
  pipelineId?: string;
  stageId?: string;
  status?: 'ACTIVE' | 'DORMANT' | 'JUNK';
  tag?: string;
  minScore?: number;
  // The advanced filters (leaf-C): any-of lists, ranges, flags. Dates are instants (ISO).
  projectIds?: string[];
  stageIds?: string[];
  /** Members, or "me"; with `unassigned` it reads "these people, or nobody". */
  ownerIds?: string[];
  sources?: string[];
  statuses?: ('ACTIVE' | 'DORMANT' | 'JUNK')[];
  tags?: string[];
  scoreMin?: number;
  scoreMax?: number;
  createdFrom?: string;
  createdTo?: string;
  lastActivityFrom?: string;
  lastActivityTo?: string;
  followUpFrom?: string;
  followUpTo?: string;
  /** Windows that follow the calendar (kept in a saved view as the name, resolved when the list is read). */
  presets?: Partial<Record<DateField, DatePreset>>;
  isNri?: boolean;
  hasEmail?: boolean;
  hasPhone?: boolean;
  city?: string;
  visit?: Exclude<VisitFilter, ''>;
  attention?: 'unattended';
  reEnquired?: boolean;
  /** With `attention`: the people who report to the viewer. */
  team?: boolean;
}

export type DatePreset = 'today' | 'week' | 'month' | 'last30';
/** The three dates a list can be limited by; each has a `<name>From` and `<name>To` filter. */
export type DateField = 'createdAt' | 'lastActivityAt' | 'followUpAt';
export const DATE_FIELDS: {
  key: DateField;
  label: string;
  from: keyof ViewFilters;
  to: keyof ViewFilters;
}[] = [
  { key: 'createdAt', label: 'Enquiry date', from: 'createdFrom', to: 'createdTo' },
  { key: 'lastActivityAt', label: 'Last activity', from: 'lastActivityFrom', to: 'lastActivityTo' },
  { key: 'followUpAt', label: 'Next follow-up', from: 'followUpFrom', to: 'followUpTo' },
];
export const PRESET_LABEL: Record<DatePreset, string> = {
  today: 'Today',
  week: 'This week',
  month: 'This month',
  last30: 'Last 30 days',
};

/** The instants a preset covers on the organisation's calendar (first moment of the first day to the last of the last). */
export function presetWindow(preset: DatePreset, tz: string, now = new Date()): { from: string; to: string } {
  const today = dayKey(now, tz);
  const [y, m] = today.split('-').map(Number) as [number, number];
  const first = (year: number, month: number) => `${year}-${String(month).padStart(2, '0')}-01`;
  const days =
    preset === 'today'
      ? { from: today, to: today }
      : preset === 'week'
        ? { from: weekStart(today), to: addDays(weekStart(today), 6) }
        : preset === 'month'
          ? { from: first(y, m), to: addDays(m === 12 ? first(y + 1, 1) : first(y, m + 1), -1) }
          : { from: addDays(today, -29), to: today };
  return {
    from: zonedInstant(days.from, 0, tz).toISOString(),
    to: new Date(zonedInstant(addDays(days.to, 1), 0, tz).getTime() - 1).toISOString(),
  };
}

export interface SavedView {
  id: string;
  name: string;
  screen: 'LEADS' | 'PIPELINE';
  filters: ViewFilters;
  teamId: string | null;
  team: { id: string; name: string } | null;
  ownerId: string;
  owner: { id: string; displayName: string };
  mine: boolean;
  pinned: boolean;
  /** This member's own default: the view the Leads list opens with. */
  isDefault: boolean;
}

export function useSavedViews(
  query: { screen?: 'LEADS' | 'PIPELINE'; pinned?: boolean } = {},
  enabled = true,
) {
  const params = new URLSearchParams();
  if (query.screen) params.set('screen', query.screen);
  if (query.pinned) params.set('pinned', 'true');
  const qs = params.toString();
  return useQuery({
    queryKey: ['saved-views', qs],
    queryFn: () => get<SavedView[]>(`/saved-views${qs ? `?${qs}` : ''}`),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Filters → query string for /leads or the board; the owner "me" becomes the viewer's own id, lists repeat the
 * parameter, and a calendar preset becomes the dates it covers today.
 */
export function filterQuery(
  filters: ViewFilters,
  meId: string | undefined,
  tz: string = DEFAULT_TIME_ZONE,
): string {
  const params = new URLSearchParams();
  const resolved: ViewFilters = { ...filters };
  for (const f of DATE_FIELDS) {
    const preset = filters.presets?.[f.key];
    if (!preset) continue;
    const w = presetWindow(preset, tz);
    (resolved as Record<string, unknown>)[f.from] = w.from;
    (resolved as Record<string, unknown>)[f.to] = w.to;
  }
  for (const [k, v] of Object.entries(resolved)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    if (k === 'pipelineId' || k === 'presets') continue;
    // Only the people who report to the viewer: meaningful with the unattended filter.
    if (k === 'team' && !resolved.attention) continue;
    if (Array.isArray(v)) {
      // "me" in ownerIds is read by the server as the caller.
      for (const item of v) params.append(k, String(item));
    } else {
      params.set(k, k === 'ownerId' && v === 'me' ? (meId ?? '') : String(v));
    }
  }
  return params.toString();
}

export interface BulkResult {
  leadId: string;
  interestId: string | null;
  name: string | null;
  ok: boolean;
  code?: string;
  message?: string;
}

export interface BulkReport {
  action: 'reassign' | 'move' | 'tag' | 'task';
  total: number;
  succeeded: number;
  failed: number;
  results: BulkResult[];
}

export interface StageHistory {
  current: { stageId: string; name: string; since: string; seconds: number };
  totals: { stageId: string; name: string; seconds: number; visits: number }[];
  transitions: {
    id: string;
    fromStage: string | null;
    toStage: string;
    secondsInStage: number | null;
    reason: string | null;
    occurredAt: string;
    actorName: string | null;
  }[];
}

export function useStageHistory(interestId: string | null) {
  return useQuery({
    queryKey: ['stage-history', interestId],
    queryFn: () => get<StageHistory>(`/interests/${interestId!}/stages`),
    enabled: Boolean(interestId),
  });
}

export interface DropOffStage {
  stageId: string;
  name: string;
  category: StageCategory;
  sortOrder: number;
  entered: number;
  left: number;
  advanced: number;
  movedBack: number;
  dropped: number;
  current: number;
  averageSecondsInStage: number | null;
  dropOffRate: number;
  conversionRate: number;
}

export interface DropOff {
  pipeline: { id: string; name: string; projectId: string | null };
  stages: DropOffStage[];
  lossReasons: { lossReasonId: string | null; name: string; count: number }[];
}

export function useDropOff(pipelineId: string | null, query: { from?: string; projectId?: string }) {
  const params = new URLSearchParams();
  if (query.from) params.set('from', query.from);
  if (query.projectId) params.set('projectId', query.projectId);
  const qs = params.toString();
  return useQuery({
    queryKey: ['drop-off', pipelineId, qs],
    queryFn: () => get<DropOff>(`/pipelines/${pipelineId!}/drop-off${qs ? `?${qs}` : ''}`),
    enabled: Boolean(pipelineId),
  });
}

export interface CalendarVisit extends Visit {
  interestId: string | null;
  transportNeeded: boolean;
  notes: string | null;
  cancelReason: string | null;
}

export interface CalendarSlot {
  id: string;
  projectId: string;
  project: { id: string; name: string };
  startAt: string;
  endAt: string;
  capacity: number;
  booked: number;
  remaining: number;
  full: boolean;
  hostId: string | null;
  host: { id: string; displayName: string } | null;
  visits: CalendarVisit[];
  otherBookings: number;
}

export interface VisitCalendar {
  from: string;
  to: string;
  slots: CalendarSlot[];
  visits: CalendarVisit[];
}

export function useVisitCalendar(query: { from: string; to: string; projectId?: string; hostId?: string }) {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  if (query.projectId) params.set('projectId', query.projectId);
  if (query.hostId) params.set('hostId', query.hostId);
  const qs = params.toString();
  return useQuery({
    queryKey: ['visit-calendar', qs],
    queryFn: () => get<VisitCalendar>(`/visit-calendar?${qs}`),
  });
}

export interface MergeSide {
  id: string;
  name: string;
  primaryPhone: string | null;
  primaryEmail: string | null;
  city: string | null;
  language: string | null;
  ownerId: string | null;
  owner: { id: string; displayName: string } | null;
  notes: string | null;
  createdAt: string;
  contacts: LeadDetail['contacts'];
  interests: {
    id: string;
    projectId: string;
    status: InterestStatus;
    project: { name: string };
    stage: { name: string };
  }[];
  _count: { tasks: number; visits: number; touches: number; consents: number };
  timeline: Activity[];
}

export type MergeField = 'name' | 'primaryPhone' | 'primaryEmail' | 'city' | 'language' | 'ownerId' | 'notes';

export interface MergePreview {
  survivor: MergeSide;
  other: MergeSide;
  fields: { field: MergeField; survivor: string | null; other: string | null; differs: boolean }[];
  sharedProjects: string[];
}

/** Budget chips (UX §4.7), in paise. */
export const BUDGET_BANDS: { label: string; min: number | null; max: number | null }[] = [
  { label: 'Under ₹30 L', min: null, max: 30_00_000_00 },
  { label: '₹30–50 L', min: 30_00_000_00, max: 50_00_000_00 },
  { label: '₹50 L–1 Cr', min: 50_00_000_00, max: 1_00_00_000_00 },
  { label: '₹1 Cr +', min: 1_00_00_000_00, max: null },
];

export const TYPOLOGIES = ['Plot', 'Villa', '1 BHK', '2 BHK', '3 BHK', '4 BHK', 'Commercial'];
